import { substanceMap, mlSubstancesArray } from './substancesAndColors';

const benzodiazepineLabels = ['Alprazolam', 'Bromazepam', 'Bromazolam', 'Clonazepam', 'Desalkylgidazepam', 'Diazepam', 'Etizolam', 'Flubromazepam', 'Flurazepam', 'Lorazepam', 'Oxazepam', 'Temazepam', 'Triazolam'];
const xylazineLabels = ['Xylazine', 'Xylazine hydrochloride'];
const fentanylLabels = ['Acetylfentanyl', 'Carfentanil', 'Despropionyl fentanyl', 'Fentanyl', 'Furanylfentanyl', 'Parafluorofentanyl', 'Remifentanil'];
const medetomidineLabels = ['Medetomidine', 'Dexmedetomidine', 'Dexmedetomidine hydrochloride'];
const benzodiazepineLabelsMap = new Map(benzodiazepineLabels.map((e, i) => [e, i]));
const xylazineLabelsMap = new Map(xylazineLabels.map((e, i) => [e, i]));
const fentanylLabelsMap = new Map(fentanylLabels.map((e, i) => [e, i]));
const approvedFentanylLabels = ['Caffeine', 'Dimethyl sulfone', 'Mannitol', 'Erythritol', 'Inositol', 'Sorbitol', 'Xylitol'];
const approvedXylazineLabels = ['Caffeine', 'Dimethyl sulfone', 'Mannitol', 'Erythritol', 'Inositol', 'Sorbitol', 'Xylitol', 'Acetylfentanyl', 'Carfentanil', 'Despropionyl fentanyl', 'Fentanyl', 'Furanylfentanyl', 'Parafluorofentanyl', 'Remifentanil'];
const approvedBenzodiazepineLabels = ['Caffeine', 'Dimethyl sulfone', 'Mannitol', 'Erythritol', 'Inositol', 'Sorbitol', 'Xylitol', 'Acetylfentanyl', 'Carfentanil', 'Despropionyl fentanyl', 'Fentanyl', 'Furanylfentanyl', 'Parafluorofentanyl', 'Remifentanil', 'Lactose', 'alpha-Lactose'];
const approvedHydromorphoneLabels = ['Lactose', 'alpha-Lactose'];

const getMaxApprovedTraceValue = (point, label) => {
    if (fentanylLabels.includes(label)) {
        if (!approvedFentanylLabels.includes(point.label)) return null;
        return Math.max(...point.fentanylPrediction) * 100;
    }
    else if (xylazineLabels.includes(label)) {
        if (!approvedXylazineLabels.includes(point.label)) return null;
        return Math.max(...point.xylazinePrediction) * 100;
    }
    else if (benzodiazepineLabels.includes(label)) {
        if (!approvedBenzodiazepineLabels.includes(point.label)) return null;
        return point.benzodiazepinePrediction[benzodiazepineLabelsMap.get(label)] * 100;
    }
    else if (label === 'Hydromorphone') {
        if (!approvedHydromorphoneLabels.includes(point.label)) return null;
        return Math.min(...point.hydromorphonePrediction) * 100;
    } else {
        return null;
    }
}

const getRawTraceValue = (point, label) => {
    if (fentanylLabels.includes(label)) {
        return point.fentanylPrediction[fentanylLabelsMap.get(label)] * 100;
    }
    else if (xylazineLabels.includes(label)) {
        return point.xylazinePrediction[xylazineLabelsMap.get(label)] * 100;
    }
    else if (benzodiazepineLabels.includes(label)) {
        return point.benzodiazepinePrediction[benzodiazepineLabelsMap.get(label)] * 100;
    }
    else if (label === 'Hydromorphone') {
        return Math.min(...point.hydromorphonePrediction) * 100;
    } else {
        return null;
    }
}

export const computeResults = (map, traceMap, computeAll = false, currentTraceDetectionPreferences) => {

    // let hasUnknown = false;
    let filteredMap = map.filter(p => {
        // if (p.label === 'Unknown') { hasUnknown = true; }
        return !['No Signal', 'Unknown', 'Calibration'].includes(p.label);
    });

    const baseDetectedSubstances = [...new Set(filteredMap.map(p => p.label))];
    const traceDetectedSubstances = [...new Set(traceMap.filter(e => e.label).map(e => e.label))];
    const allDetectedSubstances = computeAll ? mlSubstancesArray : [...new Set([...baseDetectedSubstances, ...traceDetectedSubstances])];

    const newPointData = {};

    for (let i = 0; i < filteredMap.length; i++) {
        for (let j = 0; j < allDetectedSubstances.length; j++) {

            const substanceIndex = substanceMap.get(allDetectedSubstances[j]);
            let model1Value = filteredMap[i].result[1][substanceIndex] * 100;
            let model2Value = filteredMap[i].result[2][substanceIndex] * 100;

            if (!computeAll) {
                // Group max values for Fentanyl and Xylazine categories
                if (allDetectedSubstances[j] === 'Fentanyl') {
                    for (let k = 0; k < fentanylLabels.length; k++) {
                        const analogIndex = substanceMap.get(fentanylLabels[k]);
                        let model1AnalogValue = filteredMap[i].result[1][analogIndex] * 100;
                        let model2AnalogValue = filteredMap[i].result[2][analogIndex] * 100;
                        model1Value = model1Value < model1AnalogValue ? model1AnalogValue : model1Value;
                        model2Value = model2Value < model2AnalogValue ? model2AnalogValue : model2Value;
                    }
                } else if (allDetectedSubstances[j] === 'Xylazine') {
                    for (let k = 0; k < xylazineLabels.length; k++) {
                        const analogIndex = substanceMap.get(xylazineLabels[k]);
                        let model1AnalogValue = filteredMap[i].result[1][analogIndex] * 100;
                        let model2AnalogValue = filteredMap[i].result[2][analogIndex] * 100;
                        model1Value = model1Value < model1AnalogValue ? model1AnalogValue : model1Value;
                        model2Value = model2Value < model2AnalogValue ? model2AnalogValue : model2Value;
                    }
                } else if (allDetectedSubstances[j] === 'Medetomidine') {
                    for (let k = 0; k < medetomidineLabels.length; k++) {
                        const analogIndex = substanceMap.get(medetomidineLabels[k]);
                        let model1AnalogValue = filteredMap[i].result[1][analogIndex] * 100;
                        let model2AnalogValue = filteredMap[i].result[2][analogIndex] * 100;
                        model1Value = model1Value < model1AnalogValue ? model1AnalogValue : model1Value;
                        model2Value = model2Value < model2AnalogValue ? model2AnalogValue : model2Value;
                    }
                }
            }

            // const traceValue = !computeAll ? getMaxApprovedTraceValue(filteredMap[i], allDetectedSubstances[j]) : getRawTraceValue(filteredMap[i], allDetectedSubstances[j]);

            // const value = traceValue ? (model1Value + model2Value + traceValue) / 3 : (model1Value + model2Value) / 2;
            const value = (model1Value + model2Value) / 2;

            if (!newPointData[allDetectedSubstances[j]]) newPointData[allDetectedSubstances[j]] = [];
            newPointData[allDetectedSubstances[j]].push(value);
        }
    }

    // if (hasUnknown) newPointData['Unknown'] = [];

    const resultsArray = Object.entries(newPointData).map(([key, predictions]) => {
        return { 
            substance: key, 
            predictions: predictions, 
            maxPrediction: Math.max(...predictions),
            ...(!computeAll && { is_trace_only: ![...baseDetectedSubstances, 'Unknown'].includes(key) }),
        };
    }).sort((a, b) => b.maxPrediction - a.maxPrediction);

    if (!computeAll) {
        const traceAllowedSubstancesAndCategories = Object.entries(currentTraceDetectionPreferences).filter(([substance, include]) => include).map(([substance, include]) => substance);
        const traceAllowedSubstances = traceAllowedSubstancesAndCategories.flatMap(substanceOrCategory =>
            substanceOrCategory === 'Benzodiazepine' ? benzodiazepineLabels : substanceOrCategory
        );
        return resultsArray.filter(result => !result.is_trace_only || (result.is_trace_only && traceAllowedSubstances.includes(result.substance)));
    }

    return resultsArray;
}
